import _sumBy from "lodash/sumBy";
import { TimeRangeFilterEnum } from "organisms/EventCardList/EventCardList.types";
import { BookingRecordType, InvoiceRecordType } from "pages/Booking/Booking.types";
import { DescriptionFieldType, DescriptionType } from "pages/EventBase/EventBase.types";
import { ResponsibilityRecordType } from "pages/ResponsibilityType/ResponsibilityType.types";
import {
  AttendeeRecordType,
  ContactInfoType,
  InterestRecordType,
  LecturerRecordType,
  MinimalUserRecordType,
  MinimalUserRecordTypeWithTooltip,
  OrganizationRecordType,
  PriceRecordType,
  TodoListRecordType,
  TodoRecordType,
  UserRecordType,
} from "pages/SharedRecords.types";
import { TranslateFunction } from "react-admin";

import {
  BookingRecordWithInvoiceToSendType,
  SendEmailRelatedEntitiesType,
} from "../organisms/SendEmailDialog/SendEmailDialog.types";
import { EventDateRecordType, EventFormatEnum, EventRecordType, EventStatusEnum } from "../pages/Event/Event.types";

export function defaultFilterToQuery(search: string): { search: string } {
  return { search };
}

export function reversePricePositions(price: PriceRecordType) {
  return {
    ...price,
    positions: price.positions.map((p) => ({
      ...p,
      amount: p.amount !== undefined ? -p.amount : p.amount,
    })),
  };
}

export function remainingDescriptionFields(
  descriptionFields: DescriptionFieldType[],
  descriptions: DescriptionType[],
  addedByUserFieldIds: Set<number>,
) {
  const descriptionPresent = (field: DescriptionFieldType) => {
    const desc = descriptions.find((d) => d.slug === field.slug);
    return desc && desc.text.length > 0;
  };

  return descriptionFields.filter(
    (f) => !(f.show_per_default || addedByUserFieldIds.has(f.id) || descriptionPresent(f)),
  );
}

export function conformDescriptionsToTemplate(
  descriptions: DescriptionType[],
  descriptionFields: DescriptionFieldType[],
) {
  if (!descriptionFields || descriptionFields.length === 0) return descriptions;

  const fallBackToEmptyField = (field: DescriptionFieldType) =>
    descriptions.find((d) => d.slug === field.slug) || { text: "", slug: field.slug };

  const shownPerDefault = descriptionFields.filter((f) => f.show_per_default);
  const notShownPerDefault = descriptionFields.filter((f) => !f.show_per_default);
  const notInTemplate = descriptions.filter((d) => d.text && !descriptionFields.find((f) => f.slug === d.slug));

  return [
    ...shownPerDefault.map(fallBackToEmptyField),
    ...notShownPerDefault.map(fallBackToEmptyField),
    ...notInTemplate,
  ];
}

export function filterNonTemplateDescriptions(
  descriptions: DescriptionType[],
  descriptionFields: DescriptionFieldType[],
) {
  return descriptions.filter((d) => !descriptionFields.find((f) => f.slug === d.slug));
}

export function filterEmptyTextDescriptions(descriptions: DescriptionType[] | undefined) {
  return (descriptions || []).filter((d) => d && d.text && d.text.length > 0);
}

type ContactInfoIdentifierType = "main_contact_info" | "invoice_contact_info" | "invoice_dispatch_contact_info";

export function contactInfoDataIsPresent(
  record: Partial<UserRecordType>,
  contactInfoIdentifier: ContactInfoIdentifierType,
): boolean {
  const contactInfo: ContactInfoType | null | undefined = record[contactInfoIdentifier];
  if (!contactInfo) return false;

  let hasData = false;

  for (const [k, value] of Object.entries(contactInfo)) {
    if (k === "id") continue;
    if (value !== null) {
      hasData = true;
    }
  }

  return hasData;
}

export function nullifyEmptyContactInfos(record: UserRecordType | OrganizationRecordType) {
  const infoIdentifiers: ContactInfoIdentifierType[] = [
    "main_contact_info",
    "invoice_contact_info",
    "invoice_dispatch_contact_info",
  ];

  infoIdentifiers.forEach((contactInfoType: ContactInfoIdentifierType) => {
    if (!contactInfoDataIsPresent(record, contactInfoType)) {
      record[contactInfoType] = null;
    }
  });
  return record;
}

export function isPeriodInFuture(period: TimeRangeFilterEnum | string) {
  const futurePeriods = ["YEAR_REMAINING", "NEXT_SEVEN", "NEXT_THIRTY", "NEXT_THREE_MONTHS"];
  if (futurePeriods.includes(period)) {
    return true;
  }
  return false;
}

export function getInvoicesField<K extends keyof InvoiceRecordType>(
  invoices: InvoiceRecordType[],
  field: K,
  result: InvoiceRecordType[K][] = [],
) {
  invoices.forEach((invoice) => {
    result.push(invoice[field]);
    getInvoicesField(invoice.children, field, result);
  });

  return result;
}

export function sumInvoices(invoices: InvoiceRecordType[], { netPrice = false } = {}) {
  const finalSum = invoices.reduce((sum, invoice) => {
    const children = invoice.children;
    const hasChildren = children.length > 0;

    if (netPrice && invoice.price) {
      sum += _sumBy(invoice.price.positions, "amount");
    } else if (invoice.price) {
      sum += invoice.price.amount;
    }

    if (hasChildren) {
      sum += sumInvoices(children, { netPrice });
    }

    return sum;
  }, 0);

  return Math.round(finalSum * 100) / 100;
}

export function sumPaidInvoices(invoices: InvoiceRecordType[]) {
  const finalSum = invoices.reduce((sum, invoice) => {
    const children = invoice.children;
    const hasChildren = children.length > 0;

    if (invoice.is_paid && invoice.price) {
      sum += invoice.price.amount;
    }

    if (hasChildren) {
      sum += sumPaidInvoices(children);
    }

    return sum;
  }, 0);

  return Math.round(finalSum * 100) / 100;
}

export function getUniquePersonsWithResponsibilitiesAndTooltip(
  responsibilities: ResponsibilityRecordType[],
  translate: TranslateFunction,
  contactPersons?: MinimalUserRecordType[] | null,
) {
  const persons = responsibilities.map((r) => {
    const person: MinimalUserRecordTypeWithTooltip = {
      ...r.meta.user,
      tooltip: r.meta.responsibility_type_name,
    };
    return person;
  });

  if (contactPersons && contactPersons.length > 0) {
    contactPersons.forEach((contactPerson) => {
      const contactPersonWithTooltip: MinimalUserRecordTypeWithTooltip = {
        ...contactPerson,
        tooltip: translate("resources.contact_persons.name", 1),
      };
      persons.unshift(contactPersonWithTooltip);
    });
  }

  const uniquePersons = persons.reduce((result: MinimalUserRecordTypeWithTooltip[], currentPerson) => {
    const existingPerson = result.find((person) => person.id === currentPerson.id);

    if (existingPerson) {
      existingPerson.tooltip += `, ${currentPerson.tooltip}`;
    } else {
      currentPerson.tooltip = `${currentPerson.first_name} ${currentPerson.last_name}: ${currentPerson.tooltip}`;
      result.push(currentPerson);
    }

    return result;
  }, []);

  return uniquePersons;
}

export function convertFileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        resolve("");
      }
    };

    reader.onerror = reject;
  });
}

export function getRelatedEntitiesForEmail(
  bookings: BookingRecordWithInvoiceToSendType[],
  interests: InterestRecordType[],
  attendees: AttendeeRecordType[],
  lecturers: LecturerRecordType[],
): SendEmailRelatedEntitiesType {
  if (bookings.length > 0) {
    return { relatedEntities: bookings, relatedEntityType: "booking" };
  } else if (interests.length > 0) {
    return { relatedEntities: interests, relatedEntityType: "interest" };
  } else if (lecturers.length > 0) {
    return { relatedEntities: lecturers, relatedEntityType: "lecturer" };
  }

  return { relatedEntities: attendees, relatedEntityType: "attendee" };
}

export function findInvoiceIdForBooking(
  booking: BookingRecordType<AttendeeRecordType | number>,
  invoiceIds: number[] = [],
) {
  const invoices = booking.invoices || [];
  const invoice = findInvoiceByIds(invoices, invoiceIds);

  return invoice && invoice.id;
}

export function findInvoiceByIds(invoices: InvoiceRecordType[], ids: number[]): InvoiceRecordType | null {
  let foundInvoice: InvoiceRecordType | null = null;

  const scanForInvoice = (invoice: InvoiceRecordType) => {
    if (foundInvoice) return;

    if (ids.includes(invoice.id)) {
      foundInvoice = invoice;
    } else {
      invoice.children.forEach(scanForInvoice);
    }
  };

  invoices.forEach(scanForInvoice);

  return foundInvoice;
}

export function duplicatePrices(prices: Record<string, unknown>[]) {
  return prices.map((price) => {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      id,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      created_at,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      updated_at,
      ...duplicated
    } = price;
    duplicated.positions = (price as PriceRecordType).positions.map((pos) => {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        id,
        ...remaining
      } = pos;
      return remaining;
    });
    return duplicated;
  });
}

export function duplicateDates(dates: EventDateRecordType[]) {
  return dates.map((date) => {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      id,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      created_at,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      updated_at,
      ...remaining
    } = date;
    return remaining;
  });
}

export function duplicateResponsibilities(responsibilities: ResponsibilityRecordType[]) {
  return responsibilities.map((responsibility) => {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      id,
      ...remaining
    } = responsibility;
    return remaining;
  });
}

function duplicateTodos(todos: TodoRecordType[], eventId: number) {
  return todos.map((todo) => {
    return {
      text: todo.text,
      created_by: todo.created_by,
      assignee: todo.assignee,
      is_done: false,
      due_date: undefined,
      event: eventId,
      todo_list: undefined,
      position: todo.position,
    };
  });
}

export function duplicateTodoLists(todoLists: TodoListRecordType[], event: EventRecordType) {
  return todoLists.map((todoList) => {
    return {
      id: todoList.name === "default" ? event.default_todo_list : undefined,
      name: todoList.name,
      event: event.id,
      todos: duplicateTodos(todoList.todos, event.id),
    };
  });
}

export function omit<T = Record<string, unknown>>(object: T, omitProp: keyof T) {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [omitProp]: omitted,
    ...remaining
  } = object;
  return remaining;
}

export const defaultEvent = (eventBaseId: number, initialEventLocation?: number | null) =>
  ({
    status: EventStatusEnum.Draft,
    event_base: eventBaseId,
    prices: [],
    dates: [],
    responsibilities: [],
    group_children: [],
    location: initialEventLocation || null,
    format: EventFormatEnum.Onsite,
    lecturers: [],
    attendees_min: null,
    attendees_max: null,
    registration_begin: null,
    registration_deadline: null,
    online_invite_text: null,
    rooms: [],
    custom_fields_data: {},
    is_bookable: false,
    is_public: false,
    lecturers_may_edit_attendance: false,
    display_in_portal: true,
    is_billable: true,
    inventory_account: null,
    cost_center: null,
    cost_object: null,
    project_number: null,
  }) as Partial<EventRecordType>;

export function duplicateEvent(
  event: Partial<EventRecordType>,
  {
    withPrices,
    withDates,
    withResponsibilities,
    initialEventLocation,
    eventBaseId,
    withoutCoreData,
  }: {
    withPrices?: boolean;
    withDates?: boolean;
    withResponsibilities?: boolean;
    initialEventLocation?: string | number | null | undefined;
    eventBaseId?: number | undefined;
    withoutCoreData?: boolean | undefined;
  } = {},
) {
  // In this way ts compiler isn`t complaining
  let duplicated = defaultEvent(eventBaseId || event.event_base || 0);
  if (duplicated.event_base == 0) {
    throw new Error("eventBaseId or event.event_base is required");
  }

  if (!withoutCoreData) {
    duplicated = {
      ...duplicated,
      location: event.location || initialEventLocation || null,
      format: event.format,
      lecturers: event.lecturers,
      attendees_min: event.attendees_min,
      attendees_max: event.attendees_max,
      registration_begin: event.registration_begin,
      registration_deadline: event.registration_deadline,
      online_invite_text: event.online_invite_text,
      rooms: event.rooms,
      custom_fields_data: event.custom_fields_data,
      is_bookable: event.is_bookable,
      is_public: event.is_public,
      lecturers_may_edit_attendance: event.lecturers_may_edit_attendance,
      display_in_portal: event.display_in_portal,
      is_billable: event.is_billable,
      inventory_account: event.inventory_account,
      cost_center: event.cost_center,
      cost_object: event.cost_object,
      project_number: event.project_number,
    };
  }

  return {
    ...duplicated,
    prices: withPrices && event.prices ? (duplicatePrices(event.prices) as PriceRecordType[]) : [],
    dates: withDates && event.dates ? (duplicateDates(event.dates) as unknown as EventDateRecordType[]) : [],
    responsibilities:
      withResponsibilities && event.responsibilities
        ? (duplicateResponsibilities(event.responsibilities) as unknown as EventDateRecordType[])
        : [],
  };
}

export function sortArray<T>(arr: T[], orderBy: "asc" | "desc" = "desc", attr: keyof T): T[] {
  switch (orderBy) {
    case "desc":
    default:
      return arr.sort((a, b) => String(a[attr]).localeCompare(String(b[attr])));
    case "asc":
      return arr.sort((a, b) => String(b[attr]).localeCompare(String(a[attr])));
  }
}

export function getRelatedResourceForFrontendResource(frontendResource: string) {
  if (frontendResource === "users" || frontendResource === "attendees" || frontendResource === "lecturers") {
    return "user";
  } else {
    return frontendResource.slice(0, -1);
  }
}

export const createDateRangeValidator = (
  startFieldName: string,
  endFieldName: string,
  errorMessageKey: string,
  translate: TranslateFunction,
) => {
  return (value: string | Date | undefined, allValues: { [key: string]: unknown }) => {
    const startValue = allValues[startFieldName];
    const endValue = allValues[endFieldName];

    if (!startValue || !endValue) return undefined;

    const startDate = new Date(startValue as string);
    const endDate = new Date(endValue as string);

    if (startDate > endDate) {
      return translate(errorMessageKey);
    }

    return undefined;
  };
};

export const createNumberRangeValidator = (
  minFieldName: string,
  maxFieldName: string,
  errorMessageKey: string,
  translate: TranslateFunction,
) => {
  return (value: string | number | undefined, allValues: { [key: string]: unknown }) => {
    const minValue = allValues[minFieldName];
    const maxValue = allValues[maxFieldName];

    if (minValue == null || maxValue == null) return undefined;

    if (Number(minValue) > Number(maxValue)) {
      return translate(errorMessageKey);
    }

    return undefined;
  };
};

export const createTimeRangeValidator = (
  startFieldName: string,
  endFieldName: string,
  errorMessageKey: string,
  translate: (key: string) => string,
) => {
  return (value: string | undefined, allValues: { [key: string]: unknown }) => {
    const startValue = allValues[startFieldName];
    const endValue = allValues[endFieldName];

    if (!startValue || !endValue) return undefined;

    // Parse time strings (assuming format like "HH:MM")
    const parseTime = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(":").map(Number);
      return hours * 60 + minutes; // Convert to minutes for easy comparison
    };

    const startMinutes = parseTime(startValue as string);
    const endMinutes = parseTime(endValue as string);

    if (startMinutes > endMinutes) {
      return translate(errorMessageKey);
    }

    return undefined;
  };
};
